import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface WeeklyFunnelRow {
  brokerId: string;
  brokerName: string | null;
  weekStartDate: string;
  calls: number;
  contacts: number;
  hellopacks: number;
  lodged: number;
  settled: number;
}

export interface WeeklyFunnelBrokerTotals {
  brokerId: string;
  brokerName: string | null;
  calls: number;
  contacts: number;
  hellopacks: number;
  lodged: number;
  settled: number;
  conversionPct: number;
}

/**
 * Whether each stage is measured for this tenant at all (not window-scoped).
 * `false` means the backing source holds no rows for this company, so the UI
 * must render "not tracked" rather than a 0 it cannot justify.
 */
export interface WeeklyFunnelStageCoverage {
  calls: boolean;
  contacts: boolean;
  hellopacks: boolean;
  lodged: boolean;
  settled: boolean;
}

export interface WeeklyFunnelData {
  weeksBack: number;
  rangeStart: string;
  rangeEnd: string;
  perWeek: WeeklyFunnelRow[];
  perBroker: WeeklyFunnelBrokerTotals[];
  /**
   * Optional so the page keeps working against a backend that predates this
   * field: absent is treated as "covered", i.e. the previous rendering.
   */
  stageCoverage?: WeeklyFunnelStageCoverage;
}

interface UseWeeklyFunnelParams {
  companyId: string | undefined;
  officeId?: string;
  brokerId?: string;
  weeksBack?: number;
}

interface State {
  data: WeeklyFunnelData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useWeeklyFunnel({
  companyId,
  officeId,
  brokerId,
  weeksBack = 12,
}: UseWeeklyFunnelParams): State {
  const [data, setData] = useState<WeeklyFunnelData | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const fn = httpsCallable<
      {
        companyId: string;
        officeId?: string;
        brokerId?: string;
        weeksBack?: number;
      },
      WeeklyFunnelData
    >(functions, 'getWeeklyFunnel');
    setLoading(true);
    setError(null);
    // Defensive: OMIT officeId/brokerId from the payload when undefined so
    // Firebase never serializes them to `null` on the wire. The backend
    // RequestSchema also accepts null via .nullish(), but a clean payload
    // keeps the contract unambiguous.
    const payload: {
      companyId: string;
      officeId?: string;
      brokerId?: string;
      weeksBack?: number;
    } = { companyId, weeksBack };
    if (officeId) payload.officeId = officeId;
    if (brokerId) payload.brokerId = brokerId;
    fn(payload)
      .then((r) => setData(r.data))
      .catch((err) => {
        console.error('[useWeeklyFunnel] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, officeId, brokerId, weeksBack, refreshKey]);

  return { data, loading, error, refresh };
}
